import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

import {
  ASK_LUBAV_BUTTON_ENDPOINTS,
  GeneratedContent,
  ResourcePages,
  ResourcePagesType,
} from '@backrs_ui/common-ui';

import { AskLubavButtonEndpoint } from '@backrs_ui/common-ui/dist/models/nugget';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { sendMakeItPersonalAskLubavPrompt } from './makeItPersonal.thunks';
import { RootState } from '../store';

export type MakeItPersonalBadge = Readonly<{
  id: string;
  endpoint: AskLubavButtonEndpoint | undefined;
  badgeName: string;
  title: string;
  response: string;
  isLoading?: boolean;
  type: ResourcePagesType;
  generatedContent?: GeneratedContent;
}>;

type MakeItPersonalState = Readonly<{
  makeItPersonalBadges: MakeItPersonalBadge[];
  activeMakeItPersonalBadge?: MakeItPersonalBadge;
  loading?: boolean;
  error?: string;
}>;

const initialState: MakeItPersonalState = {
  makeItPersonalBadges: [
    {
      id: 'is-this-a-good-fit',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_AM_I_A_GOOD_FIT,
      badgeName: 'Ask',
      title: 'Is this school a good fit for me?',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'what-do-students-say',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_WHAT_DO_STUDENTS_SAY,
      badgeName: 'Ask',
      title: 'What do students say?',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'campus-life',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_CAMPUS_LIFE,
      badgeName: 'Ask',
      title: 'Campus life',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'example-extracurricular-profiles',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_EXAMPLE_PROFILES,
      badgeName: 'Learn',
      title: 'Example extracurricular profiles',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'how-can-i-learn-more',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_HOW_CAN_I_LEARN_MORE,
      badgeName: 'Ask',
      title: 'How can I learn more?',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'improve-my-odds',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.COLLEGE_IMPROVE_MY_ODDS,
      badgeName: 'Pursue',
      title: 'Improve my odds of admission',
      response: '',
      isLoading: false,
      type: ResourcePages.COLLEGE,
    },
    {
      id: 'improve-my-profile',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.LIFE_READINESS_IMPROVE_MY_PROFILE,
      badgeName: 'Assess',
      title: 'Improve my profile using my strengths',
      response: '',
      isLoading: false,
      type: ResourcePages.LIFE_READINESS,
    },
    {
      id: 'succeed-in-school',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.LIFE_READINESS_SUCCEED_IN_SCHOOL,
      badgeName: 'Ask',
      title: 'How can they help me succeed in school?',
      response: '',
      isLoading: false,
      type: ResourcePages.LIFE_READINESS,
    },
    {
      id: 'achieve-my-goals',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.LIFE_READINESS_ACHIEVE_MY_GOALS,
      badgeName: 'Learn',
      title: 'Help me achieve my goals',
      response: '',
      isLoading: false,
      type: ResourcePages.LIFE_READINESS,
    },
    {
      id: 'succeed-in-my-career',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.LIFE_READINESS_SUCCEED_IN_MY_CAREER,
      badgeName: 'Ask',
      title: 'How can my strengths help me in my career?',
      response: '',
      isLoading: false,
      type: ResourcePages.LIFE_READINESS,
    },
    {
      id: 'what-do-employees-say',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.EMPLOYER_WHAT_DO_EMPLOYEES_SAY,
      badgeName: 'Ask',
      title: 'What do employees say?',
      response: '',
      isLoading: false,
      type: ResourcePages.EMPLOYER,
    },
    {
      id: 'help-me-network',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.EMPLOYER_HELP_ME_NETWORK,
      badgeName: 'Connect',
      title: 'Help me network',
      response: '',
      isLoading: false,
      type: ResourcePages.EMPLOYER,
    },
    {
      id: 'tell-me-about-industry',
      endpoint: ASK_LUBAV_BUTTON_ENDPOINTS.EMPLOYER_TELL_ME_ABOUT_INDUSTRY,
      badgeName: 'Learn',
      title: 'Tell me about the industry',
      response: '',
      isLoading: false,
      type: ResourcePages.EMPLOYER,
    },
  ],
};

export const makeItPersonalSlice = createSlice({
  name: 'makeItPersonalState',
  initialState,
  reducers: {
    resetMakeItPersonal: (state) => {
      state.makeItPersonalBadges = initialState.makeItPersonalBadges;
      state.activeMakeItPersonalBadge = undefined;
    },
    setActiveMakeItPersonalBadge: (
      state,
      action: PayloadAction<MakeItPersonalBadge | undefined>,
    ) => {
      state.activeMakeItPersonalBadge = action.payload;

      // Also update the corresponding badge in the askLubavBadges array
      if (action.payload) {
        state.makeItPersonalBadges = state.makeItPersonalBadges.map((badge) =>
          badge.id === action.payload?.id ? action.payload : badge,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMakeItPersonalAskLubavPrompt.pending, (state) => {
        if (state.activeMakeItPersonalBadge) {
          state.activeMakeItPersonalBadge.isLoading = true;
        }
      })
      .addCase(
        sendMakeItPersonalAskLubavPrompt.fulfilled,
        (state, action: PayloadAction<string>) => {
          if (state.activeMakeItPersonalBadge) {
            state.activeMakeItPersonalBadge.isLoading = false;
            state.activeMakeItPersonalBadge.response = action.payload;

            state.makeItPersonalBadges = state.makeItPersonalBadges.map((badge) =>
              badge.id === state.activeMakeItPersonalBadge?.id
                ? state.activeMakeItPersonalBadge
                : badge,
            );
          }
        },
      )
      .addCase(sendMakeItPersonalAskLubavPrompt.rejected, (state) => {
        if (state.activeMakeItPersonalBadge) {
          state.activeMakeItPersonalBadge.isLoading = false;
          state.activeMakeItPersonalBadge.response = appStrings.modules.chatBox.overloadErr;
        }
      });
  },
});

export const selectMakeItPersonal = (state: RootState) => state.makeItPersonal.makeItPersonalBadges;

export const selectMakeItPersonalBadgesByType = (type: ResourcePagesType) =>
  createSelector(selectMakeItPersonal, (badges) => badges.filter((badge) => badge.type === type));

export const { resetMakeItPersonal, setActiveMakeItPersonalBadge } = makeItPersonalSlice.actions;
